import { ActionTree } from 'vuex';
import { ReportsState, ReportsAction } from './types';
import { RootState } from '@/store/types';
import { userActionDispatcher } from '../app/actions';
import {
  getReportsStatus,
  createReportConfig,
  downloadReportPdf,
  downloadReportXls,
  deleteReportConfig,
  checkReportsExistForConfig,
  getReportsForConfig
} from '@/api/bff/reports';
import { Models } from '@mtap-smartcity/api';

export const actions: ActionTree<ReportsState, RootState> = {
  [ReportsAction.FetchReportsStatus]: async ({ commit, dispatch }): Promise<void> => {
    userActionDispatcher(dispatch, 'Pobieranie raportów...', 'pending');
    try {
      const reportsStatus = await getReportsStatus();
      commit(`${ReportsAction.FetchReportsStatus}Handler`, reportsStatus);
      userActionDispatcher(dispatch, '', 'success');
    } catch (error) {
      console.error('Failed to fetch reports status:', error);
      userActionDispatcher(dispatch, 'Błąd podczas pobierania raportów', 'error');
      throw error;
    }
  },

  [ReportsAction.CreateReportConfig]: async ({ dispatch }, config): Promise<Models.Reports.Config> => {
    try {
      userActionDispatcher(dispatch, 'Tworzenie konfiguracji raportu...', 'pending');
      const newConfig = await createReportConfig(config);
      userActionDispatcher(dispatch, '', 'success');

      // Start background refresh 1 second later, but don't await it
      setTimeout(async () => {
        await dispatch(ReportsAction.FetchReportsStatus);
      }, 1000);

      return newConfig;
    } catch (error) {
      console.error('Failed to create report config:', error);
      userActionDispatcher(dispatch, 'Błąd podczas tworzenia konfiguracji', 'error');
      throw error;
    }
  },

  [ReportsAction.DownloadReportPdf]: async ({ dispatch }, reportId: number): Promise<void> => {
    try {
      userActionDispatcher(dispatch, 'Pobieranie PDF...', 'pending');
      await downloadReportPdf(reportId);
      userActionDispatcher(dispatch, '', 'success');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      userActionDispatcher(dispatch, 'Błąd podczas pobierania PDF', 'error');
      throw error;
    }
  },

  [ReportsAction.DownloadReportXls]: async ({ dispatch }, reportId: number): Promise<void> => {
    try {
      userActionDispatcher(dispatch, 'Pobieranie XLS...', 'pending');
      await downloadReportXls(reportId);
      userActionDispatcher(dispatch, '', 'success');
    } catch (error) {
      console.error('Failed to download XLS:', error);
      userActionDispatcher(dispatch, 'Błąd podczas pobierania XLS', 'error');
      throw error;
    }
  },

  [ReportsAction.DeleteReportConfig]: async ({ commit, dispatch }, uuid: string): Promise<void> => {
    try {
      userActionDispatcher(dispatch, 'Usuwanie konfiguracji...', 'pending');
      await deleteReportConfig(uuid);
      commit(`${ReportsAction.DeleteReportConfig}Handler`, uuid);
      userActionDispatcher(dispatch, '', 'success');
    } catch (error) {
      console.error('Failed to delete report config:', error);
      userActionDispatcher(dispatch, 'Błąd podczas usuwania konfiguracji', 'error');
      throw error;
    }
  },

  [ReportsAction.PollConfigCompletion]: async ({ commit }, configUuid: string): Promise<void> => {
    let pollCount = 0;
    let intervalId: NodeJS.Timeout;

    const poll = async () => {
      try {
        pollCount += 1;
        const { exists } = await checkReportsExistForConfig(configUuid);

        if (exists) {
          clearInterval(intervalId);
          const newReports = await getReportsForConfig(configUuid);
          commit('ADD_REPORTS', newReports);
          return;
        }

        if (pollCount >= 12) {
          clearInterval(intervalId);
          console.warn('Report generation polling timeout for config:', configUuid);
        }
      } catch (error) {
        console.error('Error polling config completion:', error);
        clearInterval(intervalId);
      }
    };

    poll();

    // First phase: poll every 2 seconds for 6 polls (12 seconds)
    intervalId = setInterval(() => {
      if (pollCount >= 6) {
        // Switch to second phase: poll every 4 seconds for remaining polls
        clearInterval(intervalId);
        intervalId = setInterval(poll, 4000);
      } else if (pollCount >= 12) {
        // third phase: poll every 30 seconds
        clearInterval(intervalId);
        intervalId = setInterval(poll, 30000);
      } else {
        poll();
      }
    }, 2000);
  }

};
